import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  QrCode,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle,
  User,
  Phone,
  Mail,
  Calendar,
  Clock,
  MapPin,
} from 'lucide-react';
import RotatingAccessQr from '../components/RotatingAccessQr';
// Static QR fallback (2026-09-15 live-QA fix): the claim response does not
// echo qr_secret, so the rotating pass can't be derived on this screen —
// render the STATIC code QR inline instead of leaving the frame empty.
import { qrImageUrl } from '../hooks/usePersonalQrCode';
import { googleMapsDestinationUrl } from '../utils/mapsLink';
// Guardar pase (ADDENDUM 2026-09-14, Item A): offline visitor-pass PNG save.
import SaveVisitorPass from '../components/SaveVisitorPass';
// Post-claim destination fix (2026-09-22, Jaime bug report): a CLAIMED
// ACCOUNT (session tokens minted) is a registered user and must land on the
// role-aware dashboard — NOT on /resident-qr. The QR pass stays reachable
// from the menu ("Mi acceso"). Shared logic with LoginPage + "/" root gate.
import { getPostLoginDestinationFromStorage } from '../lib/postLoginDestination';

/**
 * InviteLanding (Phase 2, 2026-09-12) — public /invite/:token route (NO auth).
 *
 * Core rule: access never waits behind registration. The invitee sees who
 * invited them, where, and when; confirms identity with ONE screen (full name
 * + phone required, email optional as a receipts value-exchange) and gets a
 * LIVE rotating pass rendered inline from the claim response — no password
 * wall, no OTP before first entry. The pass lives in their account afterwards
 * (phone login); we say so without forcing registration now.
 *
 * Backend contracts (defensive — features hide on 404 until backend deploys):
 *  GET  /api/visitor-invites/:token        -> { success, data:{ status, hostName, condoName, visitorName?, accessWindow } }
 *  POST /api/visitor-invites/:token/claim  -> { success, data:{ code, qr_secret, valid_from, valid_until, ... } } (409/410 claimed/expired)
 */

import { resolveApiBase } from '../lib/apiBase';

const API_URL = resolveApiBase();

interface InviteInfo {
  status: 'pending' | 'claimed' | 'expired' | string;
  hostName?: string;
  condoName?: string;
  // 2026-09-15: destination geolocation (unit geo, else condo geo) for the
  // coordinate-first "Cómo llegar" maps link.
  destination_latitude?: number | null;
  destination_longitude?: number | null;
  visitorName?: string | null;
  accessWindow?: {
    valid_from?: string | null;
    valid_until?: string | null;
    valid_from_date?: string | null;
    valid_until_date?: string | null;
  } | null;
  visitorEmail?: string | null;
}

interface ClaimedPass {
  code: string;
  qr_secret?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
}

type Phase = 'loading' | 'form' | 'claiming' | 'ready' | 'claimed' | 'expired' | 'error';

const inputCls = [
  'w-full pl-9 pr-3 py-3 border border-slate-300 rounded-xl text-base',
  'text-slate-900 bg-white placeholder-slate-400',
  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
].join(' ');

const fmtDate = (iso?: string | null) =>
  iso && !Number.isNaN(new Date(iso).getTime())
    ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : '';

const fmtDay = (d?: string | null) => {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00');
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('es-MX', { dateStyle: 'medium' });
};

export default function InviteLanding() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [pass, setPass] = useState<ClaimedPass | null>(null);
  // Senior review 2026-09-15: true ONLY when a fresh account was created with
  // the typed password (adopted accounts are never touched).
  const [passwordApplied, setPasswordApplied] = useState(false);
  // FIX 2026-09-15 (Bug A, Jaime live QA): the backend NEVER touches an
  // adopted account's password (senior-review binding). When the visitor
  // typed one but the claim adopted an existing account (created_user:false
  // -> passwordApplied:false), the auto-session (sessionReady) used to hide
  // the generic "Ya tienes cuenta" note entirely, so the typed password was
  // silently discarded and the next login failed with "credenciales
  // invalidas". Track it and always show an explicit warning.
  const [passwordIgnored, setPasswordIgnored] = useState(false);
  // Immediate access: when the backend returns a token pair, the claimant is
  // logged in the moment they leave this screen.
  const [sessionReady, setSessionReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ fullName: '', phone: '', email: '', password: '', acceptedTerms: false });
  // Claim-with-password (2026-09-15): show/hide toggle for the optional field.
  const [showPassword, setShowPassword] = useState(false);

  // Force-logout before claim (Jaime ruling 2026-09-16 03:18 UTC): the landing
  // page is public, so the browser may hold ANOTHER account's live session
  // (vecindus_auth / token / user). All auth + context keys are purged BEFORE
  // the claim API call; claim tokens are persisted only after a successful
  // response. Flag drives the subtle "previous session closed" notice.
  const [hadPreviousSession, setHadPreviousSession] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setPhase('error'); setErrorMsg('Enlace de invitación inválido.'); return; }
    setPhase('loading');
    setErrorMsg(null);
    try {
      const res = await axios.get(`${API_URL}/visitor-invites/${token}`);
      const data = (res.data?.data ?? res.data) as InviteInfo;
      setInfo(data);
      // Issue 2 (2026-09-15) / claim-UX fix (2026-09-15, Jaime live QA): when
      // the invite carries a host-provided visitor email, pre-fill the claim
      // form. The field is EDITABLE (not read-only) — the visitor may replace
      // or clear it; whatever they leave is sent as `email` in the claim body
      // (backend: optional, stored on visitor_profiles, latest-wins).
      // NOTE: guard on `data`, not the stale `info` state.
      if (data?.visitorEmail) {
        setForm((f) => (f.email ? f : { ...f, email: data.visitorEmail as string }));
      }
      if (data?.status === 'pending') setPhase('form');
      else if (data?.status === 'claimed') setPhase('claimed');
      else if (data?.status === 'expired') setPhase('expired');
      else { setPhase('error'); setErrorMsg('Esta invitación no está disponible.'); }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 410) setPhase('expired');
      else if (status === 409) setPhase('claimed');
      else if (status === 404) {
        // Backend not deployed yet OR bad token — same friendly state either way.
        setPhase('error');
        setErrorMsg('Esta invitación no existe o aún no está activa. Pide a tu anfitrión que te reenvíe el enlace.');
      } else {
        setPhase('error');
        setErrorMsg('No pudimos cargar la invitación. Revisa tu conexión e intenta de nuevo.');
      }
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Immediate access (senior review 2026-09-15): once the session tokens are
  // stored, give the claimant a short beat on the success screen (the pass is
  // visible + saveable), then full-navigation to Mi Acceso — AuthContext
  // re-hydrates from localStorage on the fresh page load.
  useEffect(() => {
    if (!sessionReady) return;
    // FIX 2026-09-22 (Jaime bug report): the claimant just became/adopted a
    // REGISTERED account (session tokens were minted), so the auto-navigation
    // goes to the normal role-aware dashboard, not the QR pass screen.
    // /resident-qr as the first history entry also made hardware/browser back
    // exit the app (PWA). Guests who claim WITHOUT a session never navigate.
    const t = setTimeout(() => { window.location.assign(getPostLoginDestinationFromStorage()); }, 4000);
    return () => clearTimeout(t);
  }, [sessionReady]);

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    // Design update 2026-09-15 02:17: email is REQUIRED (follow-up channel)
    // and T&C consent is REQUIRED. The visitor may edit (not clear) a
    // host-prefilled email. Password stays OPTIONAL.
    if (!form.fullName.trim() || !form.phone.trim()) {
      setErrorMsg('Escribe tu nombre completo y tu teléfono.');
      return;
    }
    if (!form.email.trim()) {
      setErrorMsg('Escribe tu correo — lo usamos para enviarte avisos de tu visita.');
      return;
    }
    if (!form.acceptedTerms) {
      setErrorMsg('Acepta los Términos y Condiciones y la Política de Privacidad para continuar.');
      return;
    }
    // Force-logout (Jaime ruling 2026-09-16 03:18 UTC): full local logout
    // BEFORE the claim request — every auth/session key AuthContext and the
    // axios interceptor read (vecindus_auth, token, user) plus all
    // per-account context keys. Without this, a stale session token could
    // revive the previous account's context even after the context purge.
    const hadSession = (() => {
      try { return Boolean(localStorage.getItem('vecindus_auth') || localStorage.getItem('token')); } catch { return false; }
    })();
    setHadPreviousSession(hadSession);
    try {
      ['vecindus_auth', 'token', 'user',
       'vecindus_active_condo', 'vecindus_active_owned_unit', 'refreshToken', 'vecindus_active_context',
       'vecindus_condo_name', 'vecindus_lens', 'vecindus_2fa_pending']
        .forEach((k) => localStorage.removeItem(k));
      Object.keys(localStorage)
        .filter((k) => k.startsWith('vecindus_entitlements:') || k.startsWith('vecindus_permissions:'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* storage optional */ }
    setPhase('claiming');
    setErrorMsg(null);
    try {
      const res = await axios.post(`${API_URL}/visitor-invites/${token}/claim`, {
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        // Editable email (claim-UX fix 2026-09-15): the visitor's value — or
        // empty when they cleared it — is always sent; backend treats empty
        // as "no email" (normEmail null-guard).
        email: form.email.trim(),
        // Optional claim password (2026-09-15): sent only when non-empty;
        // backend treats empty/absent as "stay passwordless" and enforces the
        // 8-char minimum (400 PASSWORD_TOO_SHORT).
        password: form.password,
        // Design update 2026-09-15 02:17: explicit consent flag — backend
        // rejects with 400 TERMS_REQUIRED when absent/false.
        acceptedTerms: true,
      });
      // Claim-UX fix (2026-09-15, Jaime live QA): the route returns
      // { alreadyClaimed, createdAccount, code: { id, code, valid_from, valid_until } }
      // (code as an OBJECT, and no re-fetch of GET /:token afterwards — the
      // invite is 'claimed' by now, so a re-fetch could only land in the
      // claimed/error path and wipe out a SUCCESSFUL claim). Accept both the
      // object shape and a legacy string shape; a same-phone idempotent
      // re-claim (alreadyClaimed=true WITH a code) still renders the pass.
      const raw = (res.data?.data ?? res.data) as Record<string, any>;
      const c = raw?.code;
      const claimedPass: ClaimedPass | null =
        typeof c === 'string'
          ? { code: c, qr_secret: raw?.qr_secret ?? null, valid_from: raw?.valid_from ?? null, valid_until: raw?.valid_until ?? null }
          : c && typeof c === 'object' && c.code
            ? { code: String(c.code), qr_secret: c.qr_secret ?? raw?.qr_secret ?? null, valid_from: c.valid_from ?? null, valid_until: c.valid_until ?? null }
            : null;
      if (!claimedPass?.code) {
        // 200 but no usable code: only legitimate case is an already-claimed
        // invite without a code for THIS claimant — show the friendly claimed
        // state, never the generic error.
        if (raw?.alreadyClaimed) { setPhase('claimed'); return; }
        throw new Error('Respuesta incompleta');
      }
      setPass(claimedPass);
      setPasswordApplied(Boolean(raw?.passwordApplied));
      setPasswordIgnored(!raw?.passwordApplied && Boolean(String(form.password || '').trim()));
      // Immediate access (senior review 2026-09-15): persist the minted token
      // pair to the SAME localStorage keys AuthContext reads, then a full
      // navigation re-initializes the auth context as logged in.
      // FIX 2026-09-16 (live-QA context-leak bug, Jaime report): the claim flow
      // runs in a browser that may hold ANOTHER account's session/context keys
      // (claim without password mints no tokens and the previous session stays
      // fully active; even with tokens, persisting them left the previous
      // account's context selections in place). Purge every per-account
      // context key before the fresh session re-hydrates, so the context
      // switcher can never render the previous account's condos/homes.
      // NOTE: the full session/context purge already happened BEFORE the claim
      // call (force-logout, Jaime 2026-09-16) — nothing to purge again here.
      const tokens = raw?.tokens;
      if (tokens?.accessToken) {
        const sessionUser = {
          id: raw?.user?.id || '',
          name: raw?.user?.name || form.fullName.trim(),
          phone: raw?.user?.phone || form.phone.trim(),
        };
        const payload = JSON.stringify({ user: sessionUser, token: tokens.accessToken, timestamp: Date.now() });
        localStorage.setItem('vecindus_auth', payload);
        localStorage.setItem('token', tokens.accessToken);
        localStorage.setItem('user', JSON.stringify(sessionUser));
        setSessionReady(true);
      }
      setPhase('ready');
    } catch (err: any) {
      const status = err?.response?.status;
      // Identity batch 1 (2026-09-15): a claim whose phone belongs to an
      // existing account is BLOCKED by the backend (409 PHONE_ACCOUNT_EXISTS)
      // — that is NOT the "invite already claimed" state, so show the form
      // again with the specific message instead of the claimed screen.
      if (status === 409 && err?.response?.data?.code === 'PHONE_ACCOUNT_EXISTS') {
        setPhase('form');
        setErrorMsg('Ya existe una cuenta con este teléfono. Ingresa con tu acceso actual o pide ayuda a tu anfitrión.');
      }
      else if (status === 409) setPhase('claimed');
      else if (status === 410) setPhase('expired');
      else if (status === 404) {
        // Backend not deployed yet — surface clearly without a dead end.
        setPhase('error');
        setErrorMsg('El acceso por enlace todavía no está activo. Usa el código QR que tu anfitrión compartió contigo.');
      } else {
        setPhase('form');
        setErrorMsg(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'No pudimos activar tu pase. Intenta de nuevo.');
      }
    }
  };

  // Maps link (Jaime 2026-09-15): coordinate-first — the backend landing
  // payload carries destination_latitude/destination_longitude (unit geo from
  // migration 93, else the condo's stored coordinates). Falls back to a
  // condo-name search ONLY when no geo data exists (a label query like
  // "Buena Vista — TH1" returned wrong places in live QA).
  const mapsUrl = googleMapsDestinationUrl({
    latitude: info?.destination_latitude,
    longitude: info?.destination_longitude,
    label: info?.condoName,
  });

  // --- Window line (friendly Spanish) from the invite's accessWindow ---
  const windowLine = (() => {
    const w = info?.accessWindow;
    if (!w) return '';
    if (w.valid_from && w.valid_until) {
      const from = fmtDate(w.valid_from);
      const until = fmtDate(w.valid_until);
      const sameDay = new Date(w.valid_from).toDateString() === new Date(w.valid_until).toDateString();
      return sameDay
        ? `${new Date(w.valid_from).toLocaleDateString('es-MX', { dateStyle: 'full' })}, de ${new Date(w.valid_from).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} a ${new Date(w.valid_until).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`
        : `De ${from} a ${until}`;
    }
    const range = [fmtDay(w.valid_from_date), fmtDay(w.valid_until_date)].filter(Boolean).join(' a ');
    return range ? `Válido del ${range}` : '';
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-start sm:items-center justify-center p-4">
      {/* text-slate-900 (2026-09-15 live-QA fix): this card is ALWAYS white,
          even in dark mode — pin a dark text color so inherited text (e.g. the
          static code line) is never the dark-theme near-white (#F4F4F5). */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8 mt-6 sm:mt-0 text-slate-900">
        {phase === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-10">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <p className="text-sm text-slate-500">Cargando tu invitación…</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="text-center py-6">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-2">Invitación no disponible</h1>
            <p className="text-sm text-slate-600">{errorMsg || 'Esta invitación no está disponible.'}</p>
          </div>
        )}

        {phase === 'expired' && (
          <div className="text-center py-6">
            <Clock className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-2">Esta invitación expiró</h1>
            <p className="text-sm text-slate-600">
              La ventana de acceso de esta invitación ya pasó. Pide a {info?.hostName || 'tu anfitrión'} que te envíe una nueva — toma un segundo.
            </p>
          </div>
        )}

        {phase === 'claimed' && (
          <div className="text-center py-6">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-2">Esta invitación ya fue reclamada ✅</h1>
            <p className="text-sm text-slate-600">
              El pase de esta invitación ya está activo en una cuenta de Vecindus. Si fuiste tú, entra a la app con el teléfono con el que la reclamaste: ahí puedes volver a mostrar tu QR en la caseta cuando quieras.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              ¿No recuerdas tu contraseña? En la pantalla de acceso usa <span className="font-medium">«Olvidé mi contraseña»</span> con ese mismo teléfono para recuperarla.
            </p>
          </div>
        )}

        {(phase === 'form' || phase === 'claiming') && info && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <QrCode className="w-7 h-7 text-blue-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                {info.hostName ? `${info.hostName} te invita` : 'Te invitan'}
              </h1>
              {info.condoName && (
                <p className="text-sm text-slate-600 mt-1 flex items-center justify-center gap-1">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {info.condoName}
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Cómo llegar a ${info.condoName} en Google Maps`}
                      className="ml-1 inline-flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    >
                      📍 Cómo llegar
                    </a>
                  )}
                </p>
              )}
              {windowLine && (
                <p className="text-sm text-slate-600 mt-1 flex items-center justify-center gap-1">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {windowLine}
                </p>
              )}
            </div>
            <form onSubmit={handleClaim} className="space-y-4">
              {hadPreviousSession && (
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
                  Cerramos tu sesión anterior para continuar.
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    required
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className={inputCls}
                    placeholder="Como aparece en tu identificación"
                    autoComplete="name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                    placeholder="Ej: +52 555 123 4567"
                    autoComplete="tel"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Con este teléfono entras a la app y recuperas tu pase cuando quieras.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Correo <span className="font-normal text-slate-400">(requerido — para avisos de tu visita)</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => {
                      // Email REQUIRED + not clearable (2026-09-15): the
                      // visitor may edit the value freely, but cannot empty
                      // the field — a change that would leave it blank keeps
                      // the previous value instead.
                      const v = e.target.value;
                      setForm((f) => ({ ...f, email: v.trim() === '' ? f.email : v }));
                    }}
                    className={inputCls}
                    placeholder="Ej: tu@correo.com"
                    autoComplete="email"
                  />
                </div>
                {/* Claim-UX fix (2026-09-15): host-provided email pre-fills the
                    field but stays EDITABLE — the visitor may replace or clear
                    it; their value (or empty) is sent as `email` in the claim
                    body and backend stores it on visitor_profiles (latest-wins). */}
                {info?.visitorEmail && (
                  <p className="text-xs text-slate-500 mt-1">Prellenado con el correo de tu invitación. Podemos usar otro correo si prefieres.</p>
                )}
              </div>
              {/* Claim-with-password (2026-09-15): OPTIONAL field — leaving it
                  empty keeps the passwordless flow exactly as before. */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contraseña <span className="font-normal text-slate-400">(opcional)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={inputCls}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Si la creas aquí, entrarás a la app con tu teléfono y esta contraseña.</p>
              </div>
              {/* T&C consent (design update 2026-09-15 02:17): required to
                  submit. Links point at the REAL /terms and /privacy pages —
                  no placeholder, no fabricated legal text. */}
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.acceptedTerms}
                  onChange={(e) => setForm({ ...form, acceptedTerms: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                />
                <span>
                  Acepto los{' '}
                  <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Términos y Condiciones</a>
                  {' '}y la{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Política de Privacidad</a>.
                </span>
              </label>
              {errorMsg && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={phase === 'claiming' || !form.acceptedTerms}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-3.5 rounded-xl text-base font-semibold"
              >
                {phase === 'claiming' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                {phase === 'claiming' ? 'Activando tu pase…' : 'Entrar con este pase'}
              </button>
              <p className="text-xs text-slate-400 text-center">
                Solo necesitamos tu nombre y teléfono. Nada más.
              </p>
            </form>
          </>
        )}

        {phase === 'ready' && pass && (
          <>
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                ¡Listo{(form.fullName.trim() || info?.visitorName || '').split(/\s+/)[0] ? `, ${(form.fullName.trim() || info?.visitorName || '').split(/\s+/)[0]}` : ''}! Tu pase está activo
              </h1>
              {info?.condoName && <p className="text-sm text-slate-600 mt-1">{info.condoName}</p>}
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Cómo llegar en Google Maps"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 underline underline-offset-2 mt-1"
                >
                  📍 Cómo llegar
                </a>
              )}
              {windowLine && <p className="text-sm text-slate-600">{windowLine}</p>}
            </div>
            {/* QR visibility fix (2026-09-15, Jaime live QA — the rotating
                pass was nearly invisible): bigger render size and a white
                quiet-zone frame; qrImageUrl now forces dark-on-white +
                explicit quiet zone for every QR it serves. */}
            {/* QR visibility fix (2026-09-15, Jaime live QA — the pass area was
                nearly empty on screen): the claim response carries NO qr_secret,
                so RotatingAccessQr rendered nothing here — the QR only appeared
                after "Guardar pase" (which draws pass.code). Fall back to the
                STATIC raw-code QR inline when there is no secret, and show the
                code text in readable contrast (tone='light' pins the rotating
                manual digits to dark-on-white too). */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-col items-center justify-center">
              {pass.qr_secret ? (
                <RotatingAccessQr code={pass.code} qrSecret={pass.qr_secret} size={280} tone="light" />
              ) : (
                <>
                  <img
                    src={qrImageUrl(pass.code, 280)}
                    alt="QR estático de tu pase de visitante"
                    style={{ width: 280, height: 280 }}
                  />
                  <p className="font-mono text-base text-slate-900 tracking-wider break-all text-center mt-2">
                    {pass.code}
                  </p>
                </>
              )}
            </div>
            <p className="text-sm text-slate-700 text-center mb-2 font-medium">
              Ya puedes mostrar tu QR en la caseta. El código cambia cada minuto — siempre la pantalla actual.
            </p>
            {/* Guardar pase (ADDENDUM 2026-09-14, Item A): the saved image is
                the STATIC raw_code QR (pass.code — never the rotating VQR1
                frame), so it stays valid offline at the gate. */}
            <div className="mt-3 mb-3">
              <SaveVisitorPass
                qrCode={pass.code}
                pass={{
                  qrCode: pass.code,
                  title: form.fullName.trim() || info?.visitorName || 'Visitante',
                  lines: (() => {
                    // Destination coords-first (2026-09-15 live-QA fix): the
                    // pass image showed "Destino: Buena Vista" + "buscar 'Buena
                    // Vista' en Google Maps" — a label query that lands on the
                    // wrong pin. Use the landing payload's destination geo
                    // (unit coords, else condo coords) and keep the label only
                    // as fallback when the backend sends no geolocation.
                    const hasGeo = info?.destination_latitude != null && info?.destination_longitude != null;
                    const coords = hasGeo ? `${info!.destination_latitude}, ${info!.destination_longitude}` : '';
                    return [
                      info?.hostName ? `Anfitrión: ${info.hostName}` : '',
                      info?.condoName ? `Destino: ${info.condoName}` : '',
                      // Canvas PNG can't embed a clickable link — the exact
                      // coordinates are printed AND the maps URL is embedded
                      // as a small scannable QR below (pass.mapsUrl).
                      coords ? `Ubicación: ${coords}` : '',
                      !coords && info?.condoName ? `Ubicación: buscar '${info.condoName}' en Google Maps` : '',
                      windowLine || (pass.valid_until ? `Vigente hasta: ${fmtDate(pass.valid_until)}` : ''),
                    ].filter(Boolean);
                  })(),
                  // Maps QR inside the saved PNG (2026-09-15): a downloaded
                  // image can't carry a hyperlink — encode the landing
                  // payload's coords-first maps URL as a scannable QR instead.
                  mapsUrl,
                }}
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
              ¿Perdiste esta pantalla? Tu pase vive en tu cuenta de Vecindus: entra a la app con tu teléfono ({form.phone.trim()}) y muéstralo de nuevo en la caseta.
            </div>
            {passwordApplied && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 mt-2">
                Guarda tu contraseña — entra con tu teléfono y contraseña.
              </div>
            )}
            {passwordIgnored && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-xs text-amber-900 mt-2">
                Ya tenías una cuenta de antes, así que la contraseña que escribiste aquí <b>no se aplicó</b>. Entra con tu contraseña anterior (o recupérala desde "¿Olvidaste tu contraseña?").
              </div>
            )}
            {!passwordApplied && !passwordIgnored && !sessionReady && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mt-2">
                Ya tienes cuenta — usa tu acceso habitual para entrar a Vecindus.
              </div>
            )}
            {sessionReady && (
              <a
                href="/resident-qr"
                onClick={(e) => { e.preventDefault(); window.location.assign('/resident-qr'); }}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-xl text-base font-semibold"
              >
                Ir a Mi Acceso
              </a>
            )}
            {pass.valid_until && (
              <p className="text-xs text-slate-400 text-center mt-3">
                Vigente hasta: {fmtDate(pass.valid_until)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
